import type { ColDef, ColumnState, GridApi, GridReadyEvent } from "ag-grid-community";
import { useCallback, useEffect, useMemo, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { debounce, isDeepEqual } from "../app/utils";
import type { AgGridSavedState } from "../global/agGridStateSlice";
import { selectAgGridStateByKey, setAgGridState } from "../global/agGridStateSlice";

const agGridSavedStateEquals = (a: AgGridSavedState, b: AgGridSavedState) =>
  a.quickFilter === b.quickFilter && isDeepEqual(a.columnState, b.columnState) && isDeepEqual(a.filterModel, b.filterModel);

export function useAgGridReduxState<T = any>(
  key: string,
  baseColumns?: ColDef<T>[],
  options: { autoSizeOnFirstReady?: boolean } = {},
) {
  const dispatch = useDispatch();
  const saved = useSelector(selectAgGridStateByKey(key));
  const apiRef = useRef<GridApi | null>(null);
  const savedRef = useRef(saved);
  const { autoSizeOnFirstReady = true } = options;

  // LocalStorage partial state key (grid instance key)
  const LS_KEY = `${key}_partialState`;

  type PartialState = {
    columns: Array<{
      colId?: string;
      hide?: boolean;
      width?: number;
      pinned?: "left" | "right" | null;
      sort?: "asc" | "desc" | null;
    }>;
    filterModel?: any;
  };

  // Build merged columns from (priority) redux columnState -> localStorage partial -> baseColumns
  const mergedColumns: ColDef<T>[] | undefined = useMemo(() => {
    if (!baseColumns) return undefined;

    const applyPartial = (
      partial: Array<{
        colId?: string;
        hide?: boolean;
        width?: number;
        pinned?: "left" | "right" | null;
        sort?: "asc" | "desc" | null;
      }>,
    ): ColDef<T>[] => {
      const used = new Set<string>();
      const ordered: ColDef<T>[] = [];
      for (const s of partial) {
        const id = s.colId;
        if (!id) continue;
        const base = baseColumns.find((c) => (c.colId || (c as any).field) === id);
        if (!base) continue;
        const hasSavedWidth = typeof s.width === "number" && s.width > 0;
        used.add(id);
        ordered.push({
          ...base,
          hide: typeof s.hide === "boolean" ? s.hide : base.hide,
          width: hasSavedWidth ? s.width : base.width,
          flex: hasSavedWidth ? undefined : base.flex,
          pinned: s.pinned === "left" || s.pinned === "right" ? s.pinned : base.pinned,
          sort: s.sort,
        });
      }
      for (const base of baseColumns) {
        const keyId = base.colId || (base as any).field;
        if (!keyId || used.has(keyId)) continue;
        ordered.push(base);
      }
      return ordered;
    };

    // 1. Redux full columnState present -> convert to partial and merge
    if (saved.columnState && Array.isArray(saved.columnState)) {
      const partial = (saved.columnState as ColumnState[]).map((c) => ({
        colId: c.colId,
        hide: c.hide,
        width: c.width,
        pinned: c.pinned === "left" || c.pinned === "right" ? c.pinned : null,
        sort: c.sort,
      }));
      return applyPartial(partial);
    }

    // 2. LocalStorage partial
    try {
      const raw = localStorage.getItem(LS_KEY);
      if (raw) {
        const parsed: PartialState = JSON.parse(raw);
        if (Array.isArray(parsed?.columns)) {
          return applyPartial(parsed.columns);
        }
      }
    } catch {
      /* ignore */
    }

    // 3. Fallback baseColumns
    return baseColumns;
  }, [baseColumns, saved.columnState]);

  const persist = useCallback(() => {
    const api = apiRef.current;
    if (!api) {
      return;
    }
    const fullState = api.getColumnState();
    const partial: PartialState = {
      columns: fullState.map((c) => ({
        colId: c.colId,
        hide: c.hide,
        width: c.width,
        pinned: c.pinned === "left" || c.pinned === "right" ? c.pinned : null,
        sort: c.sort as any,
      })),
      filterModel: api.getFilterModel(),
    };
    try {
      localStorage.setItem(LS_KEY, JSON.stringify(partial));
    } catch {
      /* ignore storage errors */
    }
    const next = {
      quickFilter: (api.getGridOption("quickFilterText") as string) ?? "",
      columnState: fullState,
      filterModel: partial.filterModel,
    };
    if (agGridSavedStateEquals(savedRef.current, next)) {
      return;
    }
    savedRef.current = next;
    dispatch(setAgGridState({ key, data: next }));
  }, [dispatch, key]);

  // Stable, cancellable persistence scheduling
  const onStateUpdated = useMemo(() => debounce(persist, 600), [persist]);

  // Cancel debounced callbacks on unmount to avoid stray updates
  useEffect(() => {
    savedRef.current = saved;
  }, [saved]);

  useEffect(() => {
    return () => {
      onStateUpdated.cancel();
    };
  }, [onStateUpdated]);

  const onGridReady = useCallback(
    (params: GridReadyEvent) => {
      apiRef.current = params.api;

      // If we had redux columnState we already merged defs; else if only localStorage partial existed we merged; so only autosize if neither.
      const hasReduxCols = saved.columnState && Array.isArray(saved.columnState);
      const hasLSCols = !!localStorage.getItem(LS_KEY);
      if (autoSizeOnFirstReady && !hasReduxCols && !hasLSCols) {
        params.api.autoSizeAllColumns();
      }

      if (saved.filterModel != null) {
        params.api.setFilterModel(saved.filterModel);
      } else {
        try {
          const raw = localStorage.getItem(LS_KEY);
          if (raw) {
            const parsed: PartialState = JSON.parse(raw);
            if (parsed?.filterModel) {
              params.api.setFilterModel(parsed.filterModel);
            }
          }
        } catch {
          /* ignore */
        }
      }

      if (saved.quickFilter != null) {
        params.api.setGridOption("quickFilterText", saved.quickFilter);
      }
    },
    [LS_KEY, autoSizeOnFirstReady, saved.columnState, saved.filterModel, saved.quickFilter],
  );

  const setQuickFilterText = useCallback(
    (text: string) => {
      apiRef.current?.setGridOption("quickFilterText", text); // programmatic => ignored by onStateUpdated
      if (savedRef.current.quickFilter === text) {
        return;
      }
      savedRef.current = { ...savedRef.current, quickFilter: text };
      dispatch(setAgGridState({ key, data: { quickFilter: text } }));
    },
    [dispatch, key],
  );

  return { onGridReady, onStateUpdated, setQuickFilterText, mergedColumns };
}
